import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Camera, Sparkles, Filter, Search, Download, X, ZoomIn, 
  ChevronLeft, ChevronRight, Copy, ExternalLink, MessageSquare,
  Clock, CheckCircle, XCircle, RefreshCw, AlertCircle, Zap,
  LayoutGrid, LayoutList, ChevronDown, Eye, Ticket, Send,
  Briefcase, Plane, Coffee, Wine, Heart, HelpCircle, FileImage, 
  User, Shirt, Timer, HardDrive, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
  Globe, Monitor, Link2, Ruler
} from 'lucide-react';
import { galleryAPI, operationsAPI, teamAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

// API Type configuration
const API_TYPE_CONFIG = {
  tryon: { icon: Camera, label: 'TryOn', color: 'blue' },
  moments: { icon: Sparkles, label: 'Moments', color: 'purple' },
  size_recommendation: { icon: Ruler, label: 'Size Rec', color: 'emerald' }
};

// Helper to get API type display info
const getApiTypeInfo = (apiType) => {
  return API_TYPE_CONFIG[apiType] || API_TYPE_CONFIG.tryon;
};

// Moment type icons mapping
const MOMENT_ICONS = {
  creative_workday: Briefcase,
  escape: Plane,
  unwind: Coffee,
  after_hours: Wine,
  gentle_celebration: Heart
};

const MOMENT_LABELS = {
  creative_workday: 'Creative Workday',
  escape: 'The Escape',
  unwind: 'Unwind',
  after_hours: 'After Hours',
  gentle_celebration: 'Gentle Celebration'
};

const STATUS_CONFIG = {
  success: { color: 'emerald', icon: CheckCircle, label: 'Success' },
  failed: { color: 'red', icon: XCircle, label: 'Failed' },
  pending: { color: 'amber', icon: Clock, label: 'Pending' },
  refunded: { color: 'blue', icon: RefreshCw, label: 'Refunded' }
};

const CACHE_STATUS_CONFIG = {
  cached: { color: 'emerald', label: 'Cached' },
  new: { color: 'blue', label: 'New' },
  rerun: { color: 'purple', label: 'Re-run' },
  refunded: { color: 'amber', label: 'Refunded' }
};

// Source configuration
const SOURCE_CONFIG = {
  API: { icon: Globe, label: 'API', color: 'blue' },
  SDK_API: { icon: Globe, label: 'SDK API', color: 'blue' },
  TRYON_STUDIO: { icon: Monitor, label: 'Studio', color: 'purple' },
  SHARE_LINK: { icon: Link2, label: 'Public Try-On', color: 'emerald' },
  share_link: { icon: Link2, label: 'Public Try-On', color: 'emerald' },  // lowercase from backend
  IN_STORE_TRYON: { icon: Briefcase, label: 'In-Store', color: 'amber', noOutputAccess: true },
  'In-Store TryOn': { icon: Briefcase, label: 'In-Store', color: 'amber', noOutputAccess: true },  // from credit transactions
  dashboard: { icon: Monitor, label: 'Studio', color: 'purple' },  // backend value for dashboard
  external_api: { icon: Globe, label: 'External API', color: 'blue' },  // backend value for external API
  INTERNAL_TEST: { icon: Zap, label: 'Test', color: 'amber' },
  internal: { icon: Zap, label: 'Internal', color: 'amber' }  // backend value for internal
};

const TICKET_CATEGORIES = [
  { value: 'output_quality', label: 'Output Quality Issue' },
  { value: 'credit_issue', label: 'Credit Debited Incorrectly' },
  { value: 'api_error', label: 'API Error / Timeout' },
  { value: 'policy_issue', label: 'Policy / Content Issue' },
  { value: 'other', label: 'Other' }
];

const Gallery = () => {
  // View mode
  const [viewMode, setViewMode] = useState('table');
  
  // Data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Fullscreen viewer state
  const [viewerState, setViewerState] = useState({
    open: false,
    images: [],
    currentIndex: 0
  });
  const [pagination, setPagination] = useState({
    page: 1, total: 0, totalPages: 0, hasNext: false, hasPrev: false
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    api_type: '',
    status: '',
    cached: '',
    source: '',
    date_range: '30d',
    sku_id: '',
    customer_id: '',
    created_by: '',
    group_id: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Team attribution options (for filter dropdowns)
  const [teamOptions, setTeamOptions] = useState({ users: [], groups: [] });
  
  // Modals state
  const [detailsModal, setDetailsModal] = useState({ open: false, item: null, artifacts: [], loading: false });
  const [ticketModal, setTicketModal] = useState({ open: false, item: null });
  
  // Ticket form state
  const [ticketForm, setTicketForm] = useState({
    category: '',
    severity: 'medium',
    description: '',
    confirm: false
  });
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted items
  const getSortedItems = useCallback(() => {
    if (!sortConfig.key) return items;
    
    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'credits_consumed') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortConfig]);

  // Sort header component
  const SortHeader = ({ label, sortKey, className = '' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <button
        onClick={() => handleSort(sortKey)}
        className={`flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900 transition-colors ${className}`}
      >
        {label}
        {isActive ? (
          sortConfig.direction === 'asc' ? 
            <ArrowUp className="w-3 h-3 text-blue-600" /> : 
            <ArrowDown className="w-3 h-3 text-blue-600" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-slate-300" />
        )}
      </button>
    );
  };

  // Open fullscreen viewer with images
  const openViewer = (images, startIndex = 0) => {
    console.log('Opening viewer with', images.length, 'images, starting at', startIndex);
    console.log('Images:', images);
    if (images && images.length > 0) {
      setViewerState({ open: true, images, currentIndex: startIndex });
    }
  };

  const closeViewer = () => {
    setViewerState({ open: false, images: [], currentIndex: 0 });
  };

  const nextImage = () => {
    setViewerState(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setViewerState(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Artifact kind icons and labels
  const ARTIFACT_CONFIG = {
    INPUT_HUMAN: { icon: User, label: 'Human Image', color: 'blue' },
    INPUT_GARMENT: { icon: Shirt, label: 'Garment Image', color: 'purple' },
    OUTPUT_TRYON: { icon: Camera, label: 'Try-On Result', color: 'emerald' },
    INPUT_FOR_MOMENTS: { icon: Camera, label: 'Input Image', color: 'blue' },
    OUTPUT_MOMENTS: { icon: Sparkles, label: 'Moments Result', color: 'purple' },
    THUMBNAIL: { icon: Image, label: 'Thumbnail', color: 'slate' }
  };

  // Open details modal and fetch artifacts
  const openDetailsModal = async (item) => {
    setDetailsModal({ open: true, item, artifacts: [], loading: true });
    
    let artifacts = [];
    
    // Try to fetch from operations API if we have operation_id
    if (item.operation_id) {
      try {
        const response = await operationsAPI.getDetail(item.operation_id);
        artifacts = response.data.artifacts || [];
      } catch (error) {
        console.log('Operation not found in new system, using legacy data');
      }
    }
    
    // If no artifacts from operations API, try to build from available URLs
    if (artifacts.length === 0) {
      const outputUrl = getOutputImageUrl(item);
      
      // For Moments, try to get parent TryOn output as input
      if (item.api_type === 'moments' && item.parent_cache_key) {
        try {
          // Fetch parent TryOn's output
          const parentResponse = await galleryAPI.getItems({ 
            cache_key: item.parent_cache_key.replace('tryon/', ''),
            limit: 1 
          });
          const parentItem = parentResponse.data?.items?.[0];
          if (parentItem) {
            const parentOutputUrl = getOutputImageUrl(parentItem);
            if (parentOutputUrl) {
              artifacts.push({
                artifact_id: 'parent-input',
                kind: 'INPUT_FOR_MOMENTS',
                url: parentOutputUrl
              });
            }
          }
        } catch (e) {
          console.log('Could not fetch parent TryOn');
        }
      }
      
      // Add output image
      if (outputUrl) {
        artifacts.push({
          artifact_id: 'output',
          kind: item.api_type === 'tryon' ? 'OUTPUT_TRYON' : 'OUTPUT_MOMENTS',
          url: outputUrl
        });
      }
    }
    
    setDetailsModal(prev => ({
      ...prev,
      artifacts,
      loading: false
    }));
  };

  const fetchGallery = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.api_type) params.api_type = filters.api_type;
      if (filters.status) params.status = filters.status;
      if (filters.cached) params.cached = filters.cached;
      if (filters.source) params.source = filters.source;
      if (filters.date_range) params.date_range = filters.date_range;
      if (filters.sku_id) params.sku_id = filters.sku_id;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.created_by) params.created_by = filters.created_by;
      if (filters.group_id) params.group_id = filters.group_id;
      if (filters.search) params.search = filters.search;
      
      const response = await galleryAPI.getItems(params);
      setItems(response.data.items || []);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages,
        hasNext: response.data.has_next,
        hasPrev: response.data.has_prev
      });
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchGallery(1);
  }, []);

  // Load team users + groups for filter dropdowns (silently no-op if not a manager)
  useEffect(() => {
    (async () => {
      try {
        const [uRes, gRes] = await Promise.all([
          teamAPI.listUsers().catch(() => ({ data: { users: [] } })),
          teamAPI.listGroups().catch(() => ({ data: { groups: [] } }))
        ]);
        setTeamOptions({
          users: uRes?.data?.users || [],
          groups: gRes?.data?.groups || []
        });
      } catch {
        // team endpoints require manager role — silently ignore for regular users
      }
    })();
  }, []);

  const applyFilters = () => {
    fetchGallery(1);
  };

  const clearFilters = () => {
    setFilters({
      api_type: '',
      status: '',
      cached: '',
      source: '',
      date_range: '30d',
      sku_id: '',
      customer_id: '',
      created_by: '',
      group_id: '',
      search: ''
    });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Download image by fetching as blob (works for cross-origin URLs)
  const downloadImage = async (url, filename) => {
    try {
      toast.loading('Downloading...', { id: 'download' });
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Downloaded!', { id: 'download' });
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Try right-click and "Save image as..."', { id: 'download' });
    }
  };

  const openTicketModal = (item) => {
    setTicketModal({ open: true, item });
    setTicketForm({ category: '', severity: 'medium', description: '', confirm: false });
  };

  const submitTicket = async () => {
    if (!ticketForm.category) {
      toast.error('Please select a category');
      return;
    }
    if (ticketForm.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    if (!ticketForm.confirm) {
      toast.error('Please confirm this ticket is related to the operation');
      return;
    }

    setSubmittingTicket(true);
    try {
      const response = await galleryAPI.createTicket({
        operation_id: ticketModal.item.request_id,
        category: ticketForm.category,
        severity: ticketForm.severity,
        description: ticketForm.description,
        confirm_operation: ticketForm.confirm
      });
      
      toast.success(`Ticket #${response.data.ticket_number} created successfully!`);
      setTicketModal({ open: false, item: null });
      fetchGallery(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const getCacheStatus = (item) => {
    if (item.refunded) return 'refunded';
    if (item.cached) return 'cached';
    if (item.is_rerun) return 'rerun';
    return 'new';
  };

  const getTicketStatus = (tickets) => {
    if (!tickets || tickets.length === 0) return null;
    const statuses = ['open', 'in_progress', 'resolved', 'rejected', 'refunded'];
    for (const status of statuses) {
      const ticket = tickets.find(t => t.status === status);
      if (ticket) return ticket;
    }
    return tickets[0];
  };

  // Get source display info
  const getSourceInfo = (source) => {
    return SOURCE_CONFIG[source] || SOURCE_CONFIG.API;
  };

  // Get output image URL (handles both old and new field names)
  // For In-Store TryOn, show garment image instead of output (partner has no access to output)
  const getOutputImageUrl = (item) => {
    if (item.is_in_store || item.source === 'IN_STORE_TRYON' || item.source === 'In-Store TryOn') {
      // For In-Store, show garment/product image
      return item.garment_image_url || null;
    }
    return item.output_image_url || item.image_url || null;
  };

  // Check if item is In-Store TryOn
  const isInStoreTryOn = (item) => {
    return item.is_in_store || item.source === 'IN_STORE_TRYON' || item.source === 'In-Store TryOn';
  };

  // Mobile Card Component
  const MobileCard = ({ item }) => {
    const statusConfig = STATUS_CONFIG[item.status?.toLowerCase()] || STATUS_CONFIG.success;
    const cacheStatus = getCacheStatus(item);
    const cacheConfig = CACHE_STATUS_CONFIG[cacheStatus];
    const sourceInfo = getSourceInfo(item.source);
    const SourceIcon = sourceInfo.icon;
    const outputUrl = getOutputImageUrl(item);
    const isInStore = isInStoreTryOn(item);
    const apiTypeInfo = getApiTypeInfo(item.api_type);
    const ApiTypeIcon = apiTypeInfo.icon;

    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
        <div className="flex gap-3">
          {/* Thumbnail */}
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 cursor-pointer relative" onClick={() => outputUrl && openViewer([{ url: outputUrl, label: isInStore ? 'Product' : 'Output' }], 0)}>
            {outputUrl ? (
              <>
                <img src={outputUrl} alt="" className="w-full h-full object-cover" />
                {isInStore && (
                  <div className="absolute inset-0 bg-amber-500/10 flex items-center justify-center">
                    <Shirt className="w-6 h-6 text-amber-600 opacity-50" />
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ApiTypeIcon className="w-6 h-6 text-slate-300" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${apiTypeInfo.color}-100 text-${apiTypeInfo.color}-700`}>
                {apiTypeInfo.label}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${cacheConfig.color}-100 text-${cacheConfig.color}-700`}>
                {cacheConfig.label}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${sourceInfo.color}-100 text-${sourceInfo.color}-700 flex items-center gap-1`}>
                <SourceIcon className="w-3 h-3" />
                {sourceInfo.label}
              </span>
            </div>
            {/* Product name for In-Store items */}
            {isInStore && item.product_name && (
              <div className="mt-1 text-xs font-medium text-slate-700 truncate">
                {item.product_name}
              </div>
            )}
            <div className="mt-1 text-xs text-slate-500">
              {item.created_at ? format(parseISO(item.created_at), 'MMM d, HH:mm') : '-'}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs">
              <span className="text-slate-500">Credits: <strong className={item.cached ? 'text-emerald-600' : ''}>{item.credits_consumed || 0}</strong></span>
              {item.sku_id && <span className="text-slate-500">SKU: {item.sku_id}</span>}
            </div>
            {item.customer_id && <div className="text-xs text-slate-400 mt-0.5">Customer: {item.customer_id}</div>}
          </div>
        </div>
        
        {/* In-Store Notice */}
        {isInStore && (
          <div className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <Eye className="w-3.5 h-3.5" />
            <span>Output image visible only to customer</span>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => openDetailsModal(item)} className="flex-1 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 flex items-center justify-center gap-1">
            <Eye className="w-3.5 h-3.5" />Details
          </button>
          {outputUrl && !isInStore && (
            <button onClick={() => downloadImage(outputUrl, `tryon_${item.request_id?.slice(0, 8)}.png`)} className="flex-1 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-1">
              <Download className="w-3.5 h-3.5" />Download
            </button>
          )}
          <button onClick={() => openTicketModal(item)} className="py-1.5 px-3 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100">
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  // Table Row Component
  const TableRow = ({ item }) => {
    const statusConfig = STATUS_CONFIG[item.status?.toLowerCase()] || STATUS_CONFIG.success;
    const cacheStatus = getCacheStatus(item);
    const cacheConfig = CACHE_STATUS_CONFIG[cacheStatus];
    const ticketInfo = getTicketStatus(item.tickets);
    const MomentIcon = item.moment_type ? MOMENT_ICONS[item.moment_type] : null;
    const sourceInfo = getSourceInfo(item.source);
    const SourceIcon = sourceInfo.icon;
    const outputUrl = getOutputImageUrl(item);
    const isInStore = isInStoreTryOn(item);
    const apiTypeInfo = getApiTypeInfo(item.api_type);
    const ApiTypeIcon = apiTypeInfo.icon;

    const handleViewAllImages = async () => {
      const outputImageUrl = outputUrl;
      if (!item.operation_id) {
        if (outputImageUrl) openViewer([{ url: outputImageUrl, label: isInStore ? 'Product' : 'Output', kind: 'OUTPUT' }], 0);
        return;
      }
      try {
        const response = await operationsAPI.getDetail(item.operation_id);
        const artifacts = response.data.artifacts || [];
        const images = [];
        const inputHuman = artifacts.find(a => a.kind === 'INPUT_HUMAN');
        const inputGarment = artifacts.find(a => a.kind === 'INPUT_GARMENT');
        const outputTryon = artifacts.find(a => a.kind === 'OUTPUT_TRYON');
        const outputMoments = artifacts.find(a => a.kind === 'OUTPUT_MOMENTS');
        if (inputHuman?.url) images.push({ url: inputHuman.url, label: 'Human Image', kind: 'INPUT_HUMAN' });
        if (inputGarment?.url) images.push({ url: inputGarment.url, label: 'Garment Image', kind: 'INPUT_GARMENT' });
        if (outputTryon?.url) images.push({ url: outputTryon.url, label: 'Try-On Result', kind: 'OUTPUT_TRYON' });
        if (outputMoments?.url) images.push({ url: outputMoments.url, label: 'Moments Result', kind: 'OUTPUT_MOMENTS' });
        if (images.length === 0 && outputImageUrl) images.push({ url: outputImageUrl, label: isInStore ? 'Product' : 'Output', kind: 'OUTPUT' });
        if (images.length > 0) openViewer(images, 0);
      } catch {
        if (outputImageUrl) openViewer([{ url: outputImageUrl, label: isInStore ? 'Product' : 'Output', kind: 'OUTPUT' }], 0);
      }
    };

    return (
      <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
        {/* Thumbnail */}
        <td className="p-2 sm:p-3">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-slate-100 cursor-pointer relative group" onClick={handleViewAllImages}>
            {outputUrl ? (
              <>
                <img src={outputUrl} alt="" className="w-full h-full object-cover" />
                {isInStore && (
                  <div className="absolute bottom-0 right-0 bg-amber-500 p-0.5 rounded-tl">
                    <Shirt className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ApiTypeIcon className="w-4 sm:w-6 h-4 sm:h-6 text-slate-300" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <ZoomIn className="w-4 h-4 text-white" />
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="p-2 sm:p-3">
          <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-${apiTypeInfo.color}-100 text-${apiTypeInfo.color}-700`}>
            <ApiTypeIcon className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
            <span className="hidden sm:inline">{apiTypeInfo.label}</span>
          </span>
        </td>

        {/* Source */}
        <td className="p-2 sm:p-3">
          <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-${sourceInfo.color}-100 text-${sourceInfo.color}-700`}>
            <SourceIcon className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
            <span className="hidden sm:inline">{sourceInfo.label}</span>
          </span>
        </td>

        {/* Created By (team member or admin) */}
        <td className="p-2 sm:p-3 hidden md:table-cell">
          {item.created_by_name ? (
            <div className="flex flex-col leading-tight">
              <span className="text-xs sm:text-sm text-slate-800 font-medium truncate max-w-[140px]" title={item.created_by_name}>
                {item.created_by_name}
              </span>
              {item.team_role && (
                <span className={`text-[10px] font-medium mt-0.5 ${
                  item.team_role === 'owner' ? 'text-slate-400'
                  : item.team_role === 'sub_admin' ? 'text-indigo-600'
                  : 'text-emerald-600'
                }`}>
                  {item.team_role === 'owner' ? 'Admin'
                    : item.team_role === 'sub_admin' ? 'Sub-Admin' : 'User'}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-300">—</span>
          )}
        </td>

        {/* Group (from team member or in-store link) */}
        <td className="p-2 sm:p-3 hidden lg:table-cell">
          {item.group_name ? (
            <span className="text-xs sm:text-sm text-slate-700 truncate max-w-[120px] block" title={item.group_name}>
              {item.group_name}
            </span>
          ) : (
            <span className="text-xs text-slate-300">—</span>
          )}
        </td>

        {/* SKU / Product Name for In-Store */}
        <td className="p-2 sm:p-3 hidden md:table-cell">
          {isInStore && item.product_name ? (
            <span className="text-xs sm:text-sm text-amber-700 font-medium" title={item.product_name}>
              {item.product_name.length > 15 ? item.product_name.slice(0, 15) + '...' : item.product_name}
            </span>
          ) : (
            <span className="text-xs sm:text-sm text-slate-700">{item.sku_id || '-'}</span>
          )}
        </td>

        {/* Customer */}
        <td className="p-2 sm:p-3 hidden lg:table-cell">
          <span className="text-xs sm:text-sm text-slate-700 truncate max-w-[100px] block">{item.customer_id || '-'}</span>
        </td>

        {/* Moment Type */}
        <td className="p-2 sm:p-3 hidden xl:table-cell">
          {item.api_type === 'moments' && item.moment_type ? (
            <div className="flex items-center gap-1">
              {MomentIcon && <MomentIcon className="w-3 sm:w-4 h-3 sm:h-4 text-purple-500" />}
              <span className="text-xs sm:text-sm hidden xl:inline">{MOMENT_LABELS[item.moment_type]?.split(' ')[0] || item.moment_type}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-400">-</span>
          )}
        </td>

        {/* Credits */}
        <td className="p-2 sm:p-3 text-center">
          <span className={`font-semibold text-xs sm:text-sm ${item.cached ? 'text-emerald-600' : 'text-slate-700'}`}>
            {item.credits_consumed || 0}
          </span>
        </td>

        {/* Cache Status */}
        <td className="p-2 sm:p-3 hidden sm:table-cell">
          <span className={`inline-flex px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-${cacheConfig.color}-100 text-${cacheConfig.color}-700`}>
            {cacheConfig.label}
          </span>
        </td>

        {/* Status */}
        <td className="p-2 sm:p-3">
          <statusConfig.icon className={`w-4 h-4 text-${statusConfig.color}-500`} />
        </td>

        {/* Date */}
        <td className="p-2 sm:p-3 hidden sm:table-cell">
          <span className="text-[10px] sm:text-xs text-slate-500">
            {item.created_at ? format(parseISO(item.created_at), 'MMM d, HH:mm') : '-'}
          </span>
        </td>

        {/* Actions */}
        <td className="p-2 sm:p-3">
          <div className="flex items-center gap-0.5 sm:gap-1">
            <button onClick={() => openDetailsModal(item)} data-testid="view-details-btn" className="p-1 sm:p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Details">
              <Eye className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
            </button>
            {outputUrl && !isInStore && (
              <button onClick={() => downloadImage(outputUrl, `tryon_${item.request_id?.slice(0, 8)}.png`)} data-testid="download-btn" className="p-1 sm:p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Download">
                <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              </button>
            )}
            {isInStore && (
              <span className="p-1 sm:p-1.5 text-amber-400" title="Output visible only to customer">
                <Eye className="w-3.5 sm:w-4 h-3.5 sm:h-4 opacity-50" />
              </span>
            )}
            <button onClick={() => openTicketModal(item)} data-testid="create-ticket-btn" className="p-1 sm:p-1.5 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Ticket">
              <MessageSquare className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const sortedItems = getSortedItems();

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="gallery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Operations Gallery</h1>
          <p className="text-xs sm:text-sm text-slate-500">View all API operations and create support tickets</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button onClick={() => setViewMode('table')} className={`p-1.5 sm:p-2 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : 'text-slate-500'}`} title="Table View">
              <LayoutList className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 sm:p-2 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm' : 'text-slate-500'}`} title="Card View">
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => fetchGallery(pagination.page)} className="p-1.5 sm:p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg" title="Refresh">
            <RefreshCw className={`w-4 sm:w-5 h-4 sm:h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
        <button onClick={() => setShowFilters(!showFilters)} className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Filter className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
            <span className="font-medium text-sm sm:text-base">Filters</span>
            {(filters.api_type || filters.status || filters.cached || filters.source || filters.sku_id || filters.customer_id || filters.created_by || filters.group_id || filters.search) && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Active</span>
            )}
          </div>
          <ChevronDown className={`w-4 sm:w-5 h-4 sm:h-5 text-slate-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {showFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="pt-3 sm:pt-4 mt-3 sm:mt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2 sm:gap-3">
                  {/* Search */}
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">Search</label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-400" />
                      <input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="SKU, Customer, ID..." className="w-full pl-8 sm:pl-9 pr-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>

                  {/* API Type */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Type</label>
                    <select value={filters.api_type} onChange={(e) => setFilters({ ...filters, api_type: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">All</option>
                      <option value="tryon">TryOn</option>
                      <option value="moments">Moments</option>
                      <option value="size_recommendation">Size Rec</option>
                    </select>
                  </div>

                  {/* Source */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Source</label>
                    <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">All</option>
                      <option value="API">API</option>
                      <option value="TRYON_STUDIO">Studio</option>
                      <option value="SHARE_LINK">Public</option>
                      <option value="IN_STORE_TRYON">In-Store</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Status</label>
                    <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">All</option>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  {/* Cache */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Cache</label>
                    <select value={filters.cached} onChange={(e) => setFilters({ ...filters, cached: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">All</option>
                      <option value="true">Cached</option>
                      <option value="false">New</option>
                    </select>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Date</label>
                    <select value={filters.date_range} onChange={(e) => setFilters({ ...filters, date_range: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="24h">24h</option>
                      <option value="7d">7 days</option>
                      <option value="30d">30 days</option>
                      <option value="">All</option>
                    </select>
                  </div>
                </div>

                {/* SKU & Customer + Actions */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-2 sm:mt-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">SKU</label>
                    <input type="text" value={filters.sku_id} onChange={(e) => setFilters({ ...filters, sku_id: e.target.value })} placeholder="Filter SKU" className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Customer</label>
                    <input type="text" value={filters.customer_id} onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })} placeholder="Filter Customer" className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  {teamOptions.users.length > 0 && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Created By</label>
                      <select
                        value={filters.created_by}
                        onChange={(e) => setFilters({ ...filters, created_by: e.target.value })}
                        data-testid="gallery-filter-created-by"
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All members</option>
                        {teamOptions.users.map((u) => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.name} ({u.role === 'sub_admin' ? 'Sub-Admin' : 'User'})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {teamOptions.groups.length > 0 && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Group</label>
                      <select
                        value={filters.group_id}
                        onChange={(e) => setFilters({ ...filters, group_id: e.target.value })}
                        data-testid="gallery-filter-group"
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All groups</option>
                        {teamOptions.groups.map((g) => (
                          <option key={g.group_id} value={g.group_id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-span-2 flex items-end gap-2">
                    <button onClick={applyFilters} className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-1 sm:gap-2">
                      <Search className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Apply
                    </button>
                    <button onClick={clearFilters} className="px-3 sm:px-4 py-1.5 sm:py-2 text-slate-600 bg-slate-100 rounded-lg text-xs sm:text-sm hover:bg-slate-200">
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between text-xs sm:text-sm text-slate-500">
        <span>{pagination.total} operations {pagination.total > 0 && `• Page ${pagination.page}/${pagination.totalPages}`}</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-48 sm:h-64">
          <div className="w-10 sm:w-12 h-10 sm:h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : sortedItems.length > 0 ? (
        <>
          {viewMode === 'table' ? (
            <>
              {/* Mobile Cards View - show on small screens */}
              <div className="sm:hidden space-y-3">
                {sortedItems.map((item) => (
                  <MobileCard key={item.request_id} item={item} />
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="p-2 sm:p-3 text-left font-medium text-slate-600 w-16">Preview</th>
                        <th className="p-2 sm:p-3 text-left"><SortHeader label="Type" sortKey="api_type" /></th>
                        <th className="p-2 sm:p-3 text-left"><SortHeader label="Source" sortKey="source" /></th>
                        <th className="p-2 sm:p-3 text-left hidden md:table-cell"><SortHeader label="Created By" sortKey="created_by_name" /></th>
                        <th className="p-2 sm:p-3 text-left hidden lg:table-cell"><SortHeader label="Group" sortKey="group_name" /></th>
                        <th className="p-2 sm:p-3 text-left hidden md:table-cell"><SortHeader label="SKU" sortKey="sku_id" /></th>
                        <th className="p-2 sm:p-3 text-left hidden lg:table-cell"><SortHeader label="Customer" sortKey="customer_id" /></th>
                        <th className="p-2 sm:p-3 text-left hidden xl:table-cell">Moment</th>
                        <th className="p-2 sm:p-3 text-center"><SortHeader label="Credits" sortKey="credits_consumed" /></th>
                        <th className="p-2 sm:p-3 text-left hidden sm:table-cell">Cache</th>
                        <th className="p-2 sm:p-3 text-left"><SortHeader label="Status" sortKey="status" /></th>
                        <th className="p-2 sm:p-3 text-left hidden sm:table-cell"><SortHeader label="Date" sortKey="created_at" /></th>
                        <th className="p-2 sm:p-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((item) => (
                        <TableRow key={item.request_id} item={item} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {sortedItems.map((item, index) => {
                const sourceInfo = getSourceInfo(item.source);
                const SourceIcon = sourceInfo.icon;
                const cacheStatus = getCacheStatus(item);
                const cacheConfig = CACHE_STATUS_CONFIG[cacheStatus];
                const cardOutputUrl = getOutputImageUrl(item);
                const cardApiTypeInfo = getApiTypeInfo(item.api_type);
                const CardApiTypeIcon = cardApiTypeInfo.icon;
                
                return (
                  <motion.div key={item.request_id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.02 }} className="bg-white border border-slate-200 rounded-xl overflow-hidden group hover:shadow-lg transition-shadow">
                    <div className="aspect-square bg-slate-100 relative">
                      {cardOutputUrl ? (
                        <img src={cardOutputUrl} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => openViewer([{ url: cardOutputUrl, label: 'Output' }], 0)} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CardApiTypeIcon className="w-10 sm:w-12 h-10 sm:h-12 text-slate-300" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-${cardApiTypeInfo.color}-500 text-white`}>
                          {cardApiTypeInfo.label}
                        </span>
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-${sourceInfo.color}-500 text-white flex items-center gap-0.5`}>
                          <SourceIcon className="w-2.5 sm:w-3 h-2.5 sm:h-3" />{sourceInfo.label}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button onClick={() => openDetailsModal(item)} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 text-white"><Eye className="w-5 h-5" /></button>
                        {cardOutputUrl && <button onClick={() => downloadImage(cardOutputUrl, `tryon_${item.request_id?.slice(0, 8)}.png`)} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 text-white"><Download className="w-5 h-5" /></button>}
                        <button onClick={() => openTicketModal(item)} className="p-2 bg-blue-500/80 rounded-lg hover:bg-blue-500 text-white"><MessageSquare className="w-5 h-5" /></button>
                      </div>
                    </div>
                    <div className="p-2 sm:p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <code className="text-[10px] sm:text-xs bg-slate-100 px-1 sm:px-1.5 py-0.5 rounded font-mono text-slate-600">{item.request_id?.slice(0, 8)}</code>
                        <span className={`font-semibold text-xs sm:text-sm ${item.cached ? 'text-emerald-600' : ''}`}>{item.credits_consumed || 0} cr</span>
                      </div>
                      {item.sku_id && <div className="text-[10px] sm:text-xs text-slate-500 truncate">SKU: {item.sku_id}</div>}
                      {item.customer_id && <div className="text-[10px] sm:text-xs text-slate-400 truncate">Customer: {item.customer_id}</div>}
                      <div className="text-[10px] sm:text-xs text-slate-400">{item.created_at ? format(parseISO(item.created_at), 'MMM d, HH:mm') : '-'}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => fetchGallery(pagination.page - 1)} disabled={!pagination.hasPrev} className="p-1.5 sm:p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50">
                <ChevronLeft className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
              <span className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-600">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button onClick={() => fetchGallery(pagination.page + 1)} disabled={!pagination.hasNext} className="p-1.5 sm:p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50">
                <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-12 text-center">
          <Image className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-3 sm:mb-4 text-slate-300" />
          <h3 className="text-lg sm:text-xl font-semibold text-slate-700 mb-2">No operations found</h3>
          <p className="text-sm text-slate-500 mb-4 sm:mb-6">
            {filters.search || filters.api_type || filters.status ? 'Try adjusting your filters' : 'Your TryOn and Moments operations will appear here'}
          </p>
          <a href="/partners/tryon" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            <Zap className="w-4 h-4" />Go to TryOn Studio
          </a>
        </div>
      )}

      {/* Image Viewer Modal - Higher z-index to appear over other modals */}
      <AnimatePresence>
        {viewerState.open && viewerState.images.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-2 sm:p-4" onClick={closeViewer}>
            <button onClick={closeViewer} className="absolute top-2 sm:top-4 right-2 sm:right-4 p-2 text-white/80 hover:text-white z-10"><X className="w-6 sm:w-8 h-6 sm:h-8" /></button>
            {viewerState.images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10"><ChevronLeft className="w-6 sm:w-8 h-6 sm:h-8" /></button>
                <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10"><ChevronRight className="w-6 sm:w-8 h-6 sm:h-8" /></button>
              </>
            )}
            <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <div className="mb-2 sm:mb-4 flex items-center gap-2 sm:gap-4">
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 rounded-full text-white text-sm sm:text-base font-medium">{viewerState.images[viewerState.currentIndex]?.label}</span>
                {viewerState.images.length > 1 && <span className="text-white/60 text-xs sm:text-sm">{viewerState.currentIndex + 1} / {viewerState.images.length}</span>}
              </div>
              <img src={viewerState.images[viewerState.currentIndex]?.url} alt={viewerState.images[viewerState.currentIndex]?.label} className="max-w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg" />
              <div className="mt-2 sm:mt-4 flex items-center justify-center gap-2 sm:gap-4">
                <button 
                  onClick={() => downloadImage(
                    viewerState.images[viewerState.currentIndex]?.url, 
                    `${viewerState.images[viewerState.currentIndex]?.label?.replace(/\s+/g, '_') || 'image'}.png`
                  )} 
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                >
                  <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Download
                </button>
                <button onClick={() => copyToClipboard(viewerState.images[viewerState.currentIndex]?.url, 'URL')} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"><Copy className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Copy URL</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Details Modal - with input/output images */}
      <AnimatePresence>
        {detailsModal.open && detailsModal.item && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-base sm:text-lg font-bold">Operation Details</h2>
                  <p className="text-xs sm:text-sm text-slate-500 font-mono">{(detailsModal.item.operation_id || detailsModal.item.request_id)?.slice(0, 20)}...</p>
                </div>
                <button onClick={() => setDetailsModal({ open: false, item: null, artifacts: [] })} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              
              {detailsModal.loading ? (
                <div className="p-8 sm:p-12 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
              ) : (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                  {/* Operation Info */}
                  <div className="bg-slate-50 rounded-xl p-3 sm:p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Type</span><span className="font-medium capitalize">{detailsModal.item.api_type}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Source</span><span className="font-medium">{SOURCE_CONFIG[detailsModal.item.source]?.label || 'API'}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Credits</span><span className="font-semibold">{detailsModal.item.credits_consumed || 0}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Cached</span><span className={detailsModal.item.cached ? 'text-emerald-600 font-medium' : ''}>{detailsModal.item.cached ? 'Yes' : 'No'}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">SKU</span><span>{detailsModal.item.sku_id || '-'}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Customer</span><span>{detailsModal.item.customer_id || '-'}</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Duration</span><span>{detailsModal.item.latency_ms || '-'}ms</span></div>
                      <div><span className="text-slate-500 block text-[10px] sm:text-xs">Date</span><span className="text-[10px] sm:text-xs">{detailsModal.item.created_at ? format(parseISO(detailsModal.item.created_at), 'PPp') : '-'}</span></div>
                    </div>
                  </div>
                  
                  {/* Images Section - Input & Output */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Images</h3>
                    
                    {/* Show artifacts if available */}
                    {detailsModal.artifacts && detailsModal.artifacts.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                        {detailsModal.artifacts
                          .filter(a => a.kind !== 'THUMBNAIL')
                          .sort((a, b) => {
                            const order = ['INPUT_HUMAN', 'INPUT_GARMENT', 'INPUT_FOR_MOMENTS', 'OUTPUT_TRYON', 'OUTPUT_MOMENTS'];
                            return order.indexOf(a.kind) - order.indexOf(b.kind);
                          })
                          .map((artifact, index) => {
                            const config = ARTIFACT_CONFIG[artifact.kind] || { icon: Image, label: artifact.kind, color: 'slate' };
                            const ArtifactIcon = config.icon;
                            return (
                              <div key={artifact.artifact_id || index} className="relative group cursor-pointer" onClick={() => openViewer(
                                    detailsModal.artifacts.filter(a => a.kind !== 'THUMBNAIL').map(a => ({
                                      url: a.url,
                                      label: ARTIFACT_CONFIG[a.kind]?.label || a.kind,
                                      kind: a.kind
                                    })),
                                    index
                                  )}>
                                <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium bg-${config.color}-100 text-${config.color}-700 flex items-center gap-1 z-10 pointer-events-none`}>
                                  <ArtifactIcon className="w-3 h-3" />
                                  {config.label}
                                </div>
                                <img 
                                  src={artifact.url} 
                                  alt={config.label} 
                                  className="w-full aspect-[3/4] object-cover rounded-lg border border-slate-200 hover:border-blue-400 transition-colors"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                                  <ZoomIn className="w-6 h-6 text-white" />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      /* Fallback to output_image_url if no artifacts */
                      getOutputImageUrl(detailsModal.item) ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                          <div className="relative group cursor-pointer" onClick={() => openViewer([{ url: getOutputImageUrl(detailsModal.item), label: 'Output' }], 0)}>
                            <div className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1 z-10 pointer-events-none">
                              <Camera className="w-3 h-3" />
                              Output
                            </div>
                            <img 
                              src={getOutputImageUrl(detailsModal.item)} 
                              alt="Output" 
                              className="w-full aspect-[3/4] object-cover rounded-lg border border-slate-200 hover:border-blue-400 transition-colors"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                              <ZoomIn className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-400">
                          <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No images available</p>
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 sm:gap-3 pt-4 border-t border-slate-100">
                    <button onClick={() => { setDetailsModal({ open: false, item: null, artifacts: [] }); openTicketModal(detailsModal.item); }} className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                      <MessageSquare className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Create Ticket
                    </button>
                    {getOutputImageUrl(detailsModal.item) && (
                      <button onClick={() => downloadImage(getOutputImageUrl(detailsModal.item), `tryon_${detailsModal.item.request_id?.slice(0, 8)}.png`)} className="px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                        <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Download
                      </button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ticket Modal - simplified */}
      <AnimatePresence>
        {ticketModal.open && ticketModal.item && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl sm:rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl bg-blue-100 flex items-center justify-center"><Ticket className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" /></div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold">Create Support Ticket</h2>
                    <p className="text-xs sm:text-sm text-slate-500">Report an issue</p>
                  </div>
                </div>
                <button onClick={() => setTicketModal({ open: false, item: null })} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>

              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-400">ID:</span> <span className="font-mono">{ticketModal.item.request_id?.slice(0, 12)}...</span></div>
                    <div><span className="text-slate-400">Type:</span> <span className="capitalize">{ticketModal.item.api_type}</span></div>
                    <div><span className="text-slate-400">Credits:</span> {ticketModal.item.credits_consumed || 0}</div>
                    <div><span className="text-slate-400">Source:</span> {SOURCE_CONFIG[ticketModal.item.source]?.label || 'API'}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">Category *</label>
                  <select value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })} className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select category</option>
                    {TICKET_CATEGORIES.map(cat => (<option key={cat.value} value={cat.value}>{cat.label}</option>))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">Severity</label>
                  <div className="flex gap-2">
                    {['low', 'medium', 'high'].map(level => (
                      <button key={level} onClick={() => setTicketForm({ ...ticketForm, severity: level })} className={`flex-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium capitalize transition-colors ${ticketForm.severity === level ? level === 'low' ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' : level === 'medium' ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-500' : 'bg-red-100 text-red-700 ring-2 ring-red-500' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">Description * <span className="text-slate-400 font-normal">(min 20 chars)</span></label>
                  <textarea value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} rows={3} placeholder="Describe the issue..." className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  <p className="text-xs text-slate-400 mt-1">{ticketForm.description.length}/20</p>
                </div>

                <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
                  <input type="checkbox" checked={ticketForm.confirm} onChange={(e) => setTicketForm({ ...ticketForm, confirm: e.target.checked })} className="mt-0.5 sm:mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                  <span className="text-xs sm:text-sm text-slate-600">I confirm this ticket is related to the operation shown above</span>
                </label>

                <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
                  <button onClick={() => setTicketModal({ open: false, item: null })} className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 text-xs sm:text-sm font-medium">Cancel</button>
                  <button onClick={submitTicket} disabled={submittingTicket || !ticketForm.category || ticketForm.description.length < 20 || !ticketForm.confirm} className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2">
                    {submittingTicket ? <Loader2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 animate-spin" /> : <Send className="w-3.5 sm:w-4 h-3.5 sm:h-4" />}
                    Submit
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
